import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';

export enum SweepNotificationVariant {
  Default = 'default',
  Error = 'error',
  Success = 'success',
  Warning = 'Warning',
  Info = 'info',
  Loading = 'loading',
}

export interface NotificationAction {
  actionText: string;
  reduxAction: AnyAction;
}

export type SweepNotification = {
  key: string;
  message: string;
  action?: NotificationAction;
  variant: SweepNotificationVariant;
  dismissed?: boolean;
  keepOpen?: boolean;
  details?: string;
  linkUrl?: string;
  linkText?: string;
  linkOpenInNewTab?: boolean; // true = window.open, false = React Router navigate
  hideClose?: boolean;
  autoHideDuration?: number | null;
};

export interface NotificationsState {
  notifications: SweepNotification[];
}

const initialState: NotificationsState = {
  notifications: [],
};

export const notificationsReducer = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<SweepNotification>) => {
      state.notifications.push(action.payload);
    },
    updateNotification: (
      state,
      action: PayloadAction<{ key: string; updates: Partial<Omit<SweepNotification, 'key'>> }>,
    ) => {
      const notification = state.notifications.find(({ key }) => key === action.payload.key);
      if (notification) {
        Object.assign(notification, action.payload.updates);
      }
    },
    closeNotification: (state, action: PayloadAction<string>) => {
      const idx = state.notifications.findIndex(({ key }) => key === action.payload);
      if (idx >= 0) {
        state.notifications[idx].dismissed = true;
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      const idx = state.notifications.findIndex(({ key }) => key === action.payload);
      if (idx >= 0) {
        state.notifications.splice(idx, 1);
      }
    },
    clearNotifications: () => {
      return initialState;
    },
  },
});

export const {
  addNotification,
  updateNotification,
  removeNotification,
  closeNotification,
  clearNotifications,
} = notificationsReducer.actions;

export const selectNotifications = (state: RootState) => state.notifications.notifications;

export default notificationsReducer.reducer;
